// Overview-tab summary of the vendor readiness checklist.
//
// Replaces the "Onboarding & verification" placeholder, which promised six
// fields "in Vendor Program v3.0": three of them (background check, insurance
// verification, W-9) had since shipped as manual stages on the Qualifications
// tab, and the other three (territory, ownership, onboarded date) have no
// column, no DTO member and no endpoint. Summarising the data that exists
// beats renewing a promise about data that does not.
//
// Deliberately read-only — recording a stage stays on the Qualifications tab,
// so there is one writer and one place to look for the full note + evidence
// link. Stage list, status chip and attribution line are shared with that tab
// via vendorQualificationStages so the two cannot drift.

import { Link } from "react-router-dom";
import { Card } from "@/design-system/components/ui";
import { useVendorQualificationStages } from "@/hooks/portfolioAdmin/useVendorQualifications";
import {
  pendingStage,
  StageStatusChip,
  stageRecordedLabel,
  VENDOR_QUALIFICATION_STAGES,
} from "./vendorQualificationStages";
import { useDateFormatter } from "@/utils/datetime";

export function VendorReadinessCard({ vendorId }: { vendorId: string }) {
  const { tz } = useDateFormatter();
  const stagesQuery = useVendorQualificationStages(vendorId);
  const byCode = new Map(
    (stagesQuery.data ?? []).map((s) => [s.stageCode, s] as const),
  );

  return (
    <Card
      className="flex flex-col gap-space-2"
      data-testid="vendor-readiness-card"
    >
      <div className="flex items-start justify-between gap-space-2">
        <h3 className="font-display font-bold text-h3 m-0">
          Onboarding &amp; verification
        </h3>
        <Link
          to={`/portfolio-admin/vendors/${vendorId}?tab=qualifications`}
          className="text-small text-gold-shadow hover:underline shrink-0"
          data-testid="vendor-readiness-open-tab"
        >
          Record stages
        </Link>
      </div>
      <p className="text-small text-gray-mid m-0">
        Advisory readiness checklist. Each stage is recorded by staff and does
        not block a vendor status change.
      </p>

      {stagesQuery.isLoading ? (
        <p className="text-small text-gray-mid italic m-0">
          Loading readiness…
        </p>
      ) : stagesQuery.isError ? (
        <p className="text-small text-status-error m-0">
          Could not load readiness stages.
        </p>
      ) : (
        <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
          {VENDOR_QUALIFICATION_STAGES.map((s) => {
            const stage = byCode.get(s.code) ?? pendingStage(s.code);
            const recorded = stageRecordedLabel(stage, tz);
            return (
              <li
                key={s.code}
                className="flex items-start justify-between gap-space-2"
                data-testid={`vendor-readiness-stage-${s.code}`}
              >
                <div className="flex flex-col min-w-0">
                  <span className="text-small">{s.title}</span>
                  {/* Nothing but staff writes these rows, so the summary
                      names the claimant rather than implying the platform
                      verified anything. */}
                  <span
                    className="text-caption text-gray-mid"
                    data-testid={`vendor-readiness-recorded-by-${s.code}`}
                  >
                    {recorded ?? "Not recorded"}
                  </span>
                </div>
                <div className="shrink-0">
                  <StageStatusChip status={stage.status} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
